import { MListData } from '@common/interfaces';
import { SeatInfo } from '@common/interfaces/servicing-seatmap/seat-info';
import { SeatLegendDisplayInformation } from '@common/interfaces/servicing-seatmap/seat-legend-display-information';
import { BookingType } from '@lib/interfaces';
import { StaticMsgPipe } from '@lib/pipes';
import { GetOrderResponseDataAir, Type1 } from 'src/sdk-servicing';
import { AppConstants } from '@conf/app.constants';

/**
 * キャビンコード、ブッキングクラス等からキャビンクラスの名称 (ex. エコノミークラス)を取得する。
 * ※マスタ(V_LIST_DATA_CABIN_CLASS)から取得した名称を仕様
 * @param staticMsgPipe 静的文言出力用のPIPE
 * @param isInformative Informative かどうか
 * @param displayTargetSegment セグメント表示対象
 * @param cabinClasses キャビンクラス
 * @param bookingClass ブッキングクラス
 * @param isAwardBooking 特典PNR区分
 * @param isLayoutDetailModal レイアウト詳細モーダルら呼び出されたかどうか
 * @returns キャビンクラスの名称
 */
export function getClassNameByCabinCodeByDynamic(
  staticMsgPipe: StaticMsgPipe,
  isInformative: boolean,
  displayTargetSegment?: Type1,
  cabinClasses?: MListData[],
  bookingClass?: string,
  isAwardBooking?: boolean,
  isLayoutDetailModal?: boolean
) {
  if (!displayTargetSegment?.cabin || !cabinClasses || cabinClasses.length === 0) {
    return '';
  }

  // 参照の場合
  if (isInformative) {
    // 特典対象ブッキングクラス
    const awards = ['O', 'R', 'I', 'X'];
    // 特典か否か
    isAwardBooking = bookingClass ? awards.includes(bookingClass) : false;
  }

  // 有償特典区分 isAwardBooking=true(特典PNR区分)の場合、"A"。それ以外、"R" とする。
  let awardCode = isAwardBooking ? BookingType.A : BookingType.R;
  // 旅程区分　isJapanDomesticFlight = true(日本国内線)の場合、"domestic"とする。それ以外、"international" とする。
  const isJapanDomesticFlight = displayTargetSegment?.isJapanDomesticFlight || false;
  const flightCode = isJapanDomesticFlight
    ? GetOrderResponseDataAir.TripTypeEnum.Domestic
    : GetOrderResponseDataAir.TripTypeEnum.International;
  // クラス名称コード = 有償特典区分 + label.hyphen + 旅程区分 + label.hyphen + 当該セグメント情報.cabin
  const classNameCode = [awardCode, flightCode, displayTargetSegment?.cabin].join(
    staticMsgPipe.transform('label.hyphen')
  );
  // 表示内容を取得する。
  let cabinClass = cabinClasses.find((item) => item.value === classNameCode)?.display_content ?? '';

  // キャビンクラスが取得できない場合
  if (!cabinClass) {
    return '';
  }

  // レイアウト詳細モーダルからの呼び出しの場合は区切り文字＋ブッキングクラスの追加は不要ため現在のキャビンクラス名を返す
  if (isLayoutDetailModal) {
    return cabinClass;
  }

  if (isInformative) {
    // 参照の場合

    // 特典ではない、かつ、当該セグメントが日本国内区間でない場合、ブッキングクラスを追加
    if (!isAwardBooking && !isJapanDomesticFlight) {
      if (bookingClass) {
        cabinClass = [cabinClass, staticMsgPipe.transform('label.colon'), bookingClass].join('');
      }
    }
  } else {
    // 参照以外の場合
    // キャビンクラス名称の後ろに補足が必要な場合
    if (displayTargetSegment?.isNeededToSupplementBookingClass) {
      if (displayTargetSegment?.bookingClass) {
        cabinClass = [cabinClass, staticMsgPipe.transform('label.colon'), displayTargetSegment?.bookingClass].join('');
      }
    }
  }

  return cabinClass;
}

/**
 * 表示すべき凡例の情報を格納したリストを作成する
 * @param isContainedRearFacingSeat
 * @param isEconomy キャビンがエコノミークラスかどうか
 */
export function createLegendList(
  isContainedRearFacingSeat: boolean,
  isEconomy: boolean,
  isContainedPaidAsrSeat: boolean,
  isContainedCouchSeat: boolean,
  isInformative?: boolean
) {
  const legendList: SeatLegendDisplayInformation[] = [];

  if (isContainedRearFacingSeat) {
    // 後ろ向き席
    legendList.push({
      staticMessageKey: 'm_static_message-label.backSeat',
      imageUrl: AppConstants.IMG.S03_P010_S01_U__SEAT_BACK_SEAT_24,
    });
  } else {
    // 無料席
    legendList.push({
      staticMessageKey: 'm_static_message-label.freeSeat',
      imageUrl: AppConstants.IMG.S03_P010_S01_U__SEAT_FREE_SEAT_24,
    });
  }

  // 非常口席
  legendList.push({
    staticMessageKey: 'm_static_message-label.exitSeat',
    imageUrl: AppConstants.IMG.S03_P010_S01_U__SEAT_EMERGENCY_EXIT_SEAT_24,
  });

  // 選択不可席
  legendList.push({
    staticMessageKey: 'm_static_message-label.unselectable',
    imageUrl: AppConstants.IMG.S03_P010_S01_U__SEAT_UNAVAILABLE_24,
  });

  // 幼児あり席
  legendList.push({
    staticMessageKey: 'm_static_message-label.infant',
    imageUrl: AppConstants.IMG.S03_P010_S01_U__SEAT_INFANT_24,
  });

  // isInfomativeなら非表示
  if (!isInformative) {
    // 選択中席
    legendList.push({
      staticMessageKey: 'm_static_message-label.selecting',
      imageUrl: AppConstants.IMG.S03_P010_S01_U__SEAT_SELECTED_24,
    });
    // 変更前無料席
    legendList.push({
      staticMessageKey: 'm_static_message-label.changeBeforeFreeSeat',
      imageUrl: AppConstants.IMG.S03_P010_S01_U__SEAT_BEFORE_CHANGE_FREE_SEAT_24,
    });
  }
  // 指定済み変更不可席
  legendList.push({
    staticMessageKey: 'm_static_message-label.unalterableSoSelected',
    imageUrl: AppConstants.IMG.S03_P010_S01_U__SEAT_UNCHANGEABLE_24,
  });
  // 窓なし席
  legendList.push({
    staticMessageKey: 'm_static_message-label.nonWindow',
    imageUrl: AppConstants.IMG.S03_P010_S01_U__SEAT_NO_WINDOW_24,
  });
  // リクライニング制限席
  legendList.push({
    staticMessageKey: 'm_static_message-label.impossibleReclining',
    imageUrl: AppConstants.IMG.S03_P010_S01_U__SEAT_NO_RECLINING_24,
  });
  // バジネット
  legendList.push({
    staticMessageKey: 'm_static_message-label.bassinetSeat',
    imageUrl: AppConstants.IMG.S03_P010_S01_U__SEAT_BASSINET_24,
  });
  // テーブル
  legendList.push({
    staticMessageKey: 'm_static_message-label.table',
    imageUrl: AppConstants.IMG.S03_P010_S01_U__SEAT_TABLE_24,
  });
  // 化粧室
  legendList.push({
    staticMessageKey: 'm_static_message-label.restroom',
    imageUrl: AppConstants.IMG.S03_P010_S01_U__SEAT_LAVATORY_24,
  });
  // 多目的ルーム
  legendList.push({
    staticMessageKey: 'm_static_message-label.multipurposeRoom',
    imageUrl: AppConstants.IMG.S03_P010_S01_U__SEAT_MULTI_PURPOSE_ROOM_24,
  });
  // 通路
  legendList.push({
    staticMessageKey: 'm_static_message-label.passage',
    imageUrl: AppConstants.IMG.S03_P010_S01_U__SEAT_AISLE_24,
  });
  // 非常口
  legendList.push({
    staticMessageKey: 'm_static_message-label.exit',
    imageUrl: AppConstants.IMG.S03_P010_S01_U__SEAT_EMERGENCY_EXIT_24,
  });
  // 翼
  legendList.push({
    staticMessageKey: 'm_static_message-label.wing',
    imageUrl: AppConstants.IMG.S03_P010_S01_U__SEAT_WING_24,
  });
  // ギャレー
  legendList.push({
    staticMessageKey: 'm_static_message-label.galley',
    imageUrl: AppConstants.IMG.S03_P010_S01_U__SEAT_GALLEY_24,
  });
  // パーティション
  legendList.push({
    staticMessageKey: 'm_static_message-label.partition',
    imageUrl: AppConstants.IMG.S03_P010_S01_U__SEAT_PARTITION_24,
  });
  // バー
  legendList.push({
    staticMessageKey: 'm_static_message-label.barCounter',
    imageUrl: AppConstants.IMG.S03_P010_S01_U__SEAT_BAR_24,
  });
  // 階段
  legendList.push({
    staticMessageKey: 'm_static_message-label.stairs',
    imageUrl: AppConstants.IMG.S03_P010_S01_U__SEAT_STAIR_24,
  });
  return legendList;
}

/**
 * シートマップレイアウトに表示すべき凡例の情報を格納したリストを作成する
 */
export function createLegendListForSeatmapLayout() {
  const legendList: SeatLegendDisplayInformation[] = [];
  // ギャレー
  legendList.push({
    staticMessageKey: 'm_static_message-label.galley',
    imageUrl: 'assets/images/seatlayout_galley_29.png',
  });
  // バーコーナー
  legendList.push({
    staticMessageKey: 'm_static_message-label.barCounter',
    imageUrl: 'assets/images/seatlayout_bar_29.png',
  });
  // モニター
  legendList.push({
    staticMessageKey: 'm_static_message-label.monitor',
    imageUrl: 'assets/images/seatlayout_monitor_29.png',
  });
  // 非常口
  legendList.push({
    staticMessageKey: 'm_static_message-label.exit',
    imageUrl: 'assets/images/seatlayout_emergency_exit_29.png',
  });
  // 化粧室
  legendList.push({
    staticMessageKey: 'm_static_message-label.restroom',
    imageUrl: 'assets/images/seatlayout_lavatory_29.png',
  });
  // 化粧室 車いす可
  legendList.push({
    staticMessageKey: 'm_static_message-label.restroomWheelchairAvailable',
    imageUrl: 'assets/images/seatlayout_lavatory_wheelchair_29.png',
  });
  // 化粧室 おむつ交換台付き
  legendList.push({
    staticMessageKey: 'm_static_message-label.restroomWithDiaperStand',
    imageUrl: 'assets/images/seatlayout_lavatory_infant_29.png',
  });
  // 温水洗浄機能付き化粧室
  legendList.push({
    staticMessageKey: 'm_static_message-label.restroomWithMultifunctionBidet',
    imageUrl: 'assets/images/seatlayout_lavatory_hotwater_29.png',
  });
  // 多目的ルーム
  legendList.push({
    staticMessageKey: 'm_static_message-label.multipurposeRoom',
    imageUrl: 'assets/images/seatlayout_multi_room_29.png',
  });
  // 非常口座席
  legendList.push({
    staticMessageKey: 'm_static_message-label.exitSeat',
    imageUrl: 'assets/images/seatlayout_emergency_exit_seat_29.png',
  });
  // 窓なし席
  legendList.push({
    staticMessageKey: 'm_static_message-label.noWindowSeat',
    imageUrl: 'assets/images/seatlayout_no_window_seat_29.png',
  });
  // リクライニングしない席
  legendList.push({
    staticMessageKey: 'm_static_message-label.impossibleReclining',
    imageUrl: 'assets/images/seatlayout_no_reclining_seat_29.png',
  });
  // アームレストが上がらない席
  legendList.push({
    staticMessageKey: 'm_static_message-label.armrestNotMovableSeat',
    imageUrl: 'assets/images/seatlayout_no_uparm_seat_29.png',
  });
  // テーブル
  legendList.push({
    staticMessageKey: 'm_static_message-label.table',
    imageUrl: 'assets/images/seatlayout_table_29.png',
  });
  // 可動式パーテション
  legendList.push({
    staticMessageKey: 'm_static_message-label.movablePartition',
    imageUrl: 'assets/images/seatlayout_partition_29.png',
  });
  // 階段
  legendList.push({
    staticMessageKey: 'm_static_message-label.stairs',
    imageUrl: 'assets/images/seatlayout_stairs_29.png',
  });
  return legendList;
}

/**
 * カウチシート座席番号を、行番号と列番号部分に分解する
 *
 * 例）'39HJK' → { rowNumber: '39', columnNumbers: ['H', 'J', 'K'] }
 * @param key
 * @returns
 */
export function splitCouchSeatNumber(key?: string) {
  // 先頭文字の連続した数字を１つの値として抜き出し
  const regexNumber = /^[0-9]+/g;
  const rowNumbers = key?.match(regexNumber);

  // アルファベットを１文字ずつ別の値として抜き出し
  const regexAlphabet = /[A-Z]/g;
  const columnNumbers = key?.match(regexAlphabet);
  return { rowNumber: rowNumbers?.[0], columnNumbers: columnNumbers };
}

/**
 * シート座席番号を、行番号と列番号部分に分解する
 *
 * 例）'39H' → { rowNumber: '39', columnNumber: 'H' }
 * @param key
 * @returns
 */
export function splitSeatNumber(key?: string) {
  const seat = splitCouchSeatNumber(key);
  return { rowNumber: seat.rowNumber, columnNumber: seat.columnNumbers?.[0] };
}

/**
 * 配列を指定した条件にて分割し二次元配列にした [シャローコピー](https://developer.mozilla.org/ja/docs/Glossary/Shallow_copy) を作成する。
 *
 * 下記は使用例。
 *
 * ```typescript
 * const originArray = [{ text: 'a' }, { text: 'b' }, { text: 'c' }, { text: 'd' }];
 * const splitted = splitArray(originArray, (value) => value.text === 'c');
 * console.log(splitted)
 * => [[{"text": "a"}, {"text": "b"}], [{"text": "d"}]]
 * ```
 *
 * @param originArray 元となる配列
 * @param separatorFunc 区切りとなる要素を指定するための条件
 * @returns 分割後の二次元配列
 */
export function splitArray<T>(originArray: T[], separatorFunc: (originArrayValue: T) => boolean) {
  const lastIndex = originArray.length - 1;
  const results: T[][] = [];
  let tmp: T[] = [];
  originArray.forEach((value, index) => {
    if (separatorFunc(value)) {
      results.push(tmp);
      tmp = new Array();
    } else {
      tmp.push(value);
      if (lastIndex === index) {
        results.push(tmp);
      }
    }
  });
  return results;
}

/**
 * 座席番号の取得
 * @param passengerId 搭乗者ID
 * @param selectedSeatInfoList 選択中座席情報
 * @param displayTargetSegmentId 表示対象セグメントID
 * @returns 座席番号
 */
export function getSeatNumber(passengerId: string, selectedSeatInfoList: SeatInfo[], displayTargetSegmentId: string) {
  if (
    !!selectedSeatInfoList
      ?.find((s) => s.segmentId === displayTargetSegmentId)
      ?.passengerList.find((p) => p.id === passengerId)?.seatNumberList &&
    selectedSeatInfoList
      ?.find((s) => s.segmentId === displayTargetSegmentId)
      ?.passengerList.find((p) => p.id === passengerId)?.seatNumberList?.length! > 0
  ) {
    let seatNumber = '';
    seatNumber = selectedSeatInfoList
      ?.find((s) => s.segmentId === displayTargetSegmentId)
      ?.passengerList.find((p) => p.id === passengerId)?.seatNumberList![0].seatNumber!;

    if (
      !!selectedSeatInfoList
        ?.find((s) => s.segmentId === displayTargetSegmentId)
        ?.passengerList.find((p) => p.id === passengerId)?.couchSeatNumberList &&
      selectedSeatInfoList
        ?.find((s) => s.segmentId === displayTargetSegmentId)
        ?.passengerList.find((p) => p.id === passengerId)?.couchSeatNumberList?.length! > 0
    ) {
      seatNumber = getSeatNumberFromCouch(
        selectedSeatInfoList
          ?.find((s) => s.segmentId === displayTargetSegmentId)
          ?.passengerList.find((p) => p.id === passengerId)?.couchSeatNumberList![0].seatNumber || '',
        seatNumber
      );
    }
    return seatNumber;
  }
  return undefined;
}

/**
 * カウチ席番号取得
 * @param couchSeatNo カウチ席番号
 * @param seatNo 座席番号
 * @returns カウチ席番号
 */
export function getSeatNumberFromCouch(couchSeatNo: string, seatNo: string): string {
  const { rowNumber, columnNumbers } = splitCouchSeatNumber(couchSeatNo);
  if (columnNumbers?.length === 3) {
    if (
      seatNo === `${rowNumber}${columnNumbers?.[0]}` ||
      seatNo === `${rowNumber}${columnNumbers?.[1]}` ||
      seatNo === `${rowNumber}${columnNumbers?.[2]}`
    ) {
      return couchSeatNo;
    }
  } else if (columnNumbers?.length === 4) {
    if (
      seatNo === `${rowNumber}${columnNumbers?.[0]}` ||
      seatNo === `${rowNumber}${columnNumbers?.[1]}` ||
      seatNo === `${rowNumber}${columnNumbers?.[2]}` ||
      seatNo === `${rowNumber}${columnNumbers?.[3]}`
    ) {
      return couchSeatNo;
    }
  }

  return seatNo;
}
